<template>
	<div class="buybox-container" ref="buyboxContainer" v-if="!loading">
		<div class="buybox-container__header">
			<h2
				v-if="!buyBoxIsUpcoming"
				class="buybox-container__header__text"
				:class="{
					'primary-color': promotedOffers.length > 0 && !buyBoxIsUpcoming,
				}"
				@click="watchNowTitleClicked"
			>
				<slot name="buybox-title">
					{{ $t('WEBAPP_WATCHNOW') }}
				</slot>
			</h2>
			<h2 v-else class="buybox-container__header__text buybox-container__header__text--upcoming">
				{{ $t('WEBAPP_COMINGSOON') }}
			</h2>
			<div class="buybox-container__header__switchers">
				<slot name="buybox-switcher" v-if="!isInternationalOffersTitle && !mergeBuyRentExpVariant">
					<BuyBoxTypeSwitcher
						:title="title"
						v-if="!hideBuyBoxSwitcher"
						:additionalContexts="additionalContexts"
					/>
				</slot>
				<BuyBoxCountrySwitcher :hideText="true" :additionalContexts="additionalContexts" />
			</div>
		</div>
		<div>
			<PromotedListBuyBox
				v-if="promotedOffers.length > 0 && !buyBoxIsUpcoming && !hidePromotedBuyBox"
				:promotedOffers="promotedOffers"
				:objectType="buyBoxObjectType"
				:title="title"
				:additionalContexts="additionalContexts"
				@watchNowTitleClicked="watchNowTitleClicked"
			/>
		</div>

		<ListBuyBoxFilter
			:id="!isEpisodeBuybox && 'regular-buybox'"
			:buyBoxFilterOptions="buyBoxFilterOptions"
			:buyboxFilterType="buyboxFilterOptimistic"
			:additionalContexts="additionalContexts"
			:isTitleUpcoming="buyBoxIsUpcoming"
			@filterClicked="handleFilterClick"
		/>

		<ListUpcomingNoOffers
			v-if="buyBoxIsUpcoming"
			:upcomingReleases="upcomingReleasesFromGroups"
			:hasUpcomingReleases="upcomingReleases.length > 0"
			:title="title"
			:filter="buyboxFilter"
			:isTitleInWatchlist="isInWatchList"
			:additionalContexts="additionalContexts"
			:seasonNumber="buyBoxSeasonNumber"
			@onNotifyMe="onNotifyMe"
		/>

		<BuyboxInternationalOffers
			v-if="isInternationalOffersActive"
			key="buybox-international-offers"
			v-observe-visibility="{
				...mrcObservableOptions,
				callback: trackInternationalOffersImpression,
			}"
			:title="title"
			:titleId="buyBoxTitleId"
			:buyboxOffers="buyboxData"
			:buyboxFilterType="buyboxFilter"
			:isEpisodeBuybox="isEpisodeBuybox"
			@onNotifyMe="onNotifyMe"
			@filterClicked="handleFilterClick"
		>
			<!-- this component is only used for the "no offer"-segment, so we don't repeat ourselves -->
			<RegularListBuyBox
				v-if="!isTitleUpcoming"
				:allOffers="allRegularOffers"
				:filter="buyboxFilter"
				:regularOffers="filteredRegularOffers"
				:title="title"
				:additionalContexts="additionalContexts"
				:upcomingReleases="upcomingReleasesFromGroups"
				:isInWatchlist="isInWatchList"
				:isTitleUpcoming="isTitleUpcoming"
				:isEpisodeBuybox="isEpisodeBuybox"
				:seasonNumber="seasonNumber"
				:episodeNumber="episodeNumber"
				:hasVODOffers="hasVODOffers"
				@onNotifyMe="onNotifyMe"
			/>
		</BuyboxInternationalOffers>

		<RegularListBuyBox
			v-else-if="!buyBoxIsUpcoming"
			:allOffers="allRegularOffers"
			:filter="buyboxFilter"
			:regularOffers="filteredRegularOffers"
			:title="title"
			:additionalContexts="additionalContexts"
			:upcomingReleases="upcomingReleasesFromGroups"
			:isInWatchlist="isInWatchList"
			:isTitleUpcoming="buyBoxIsUpcoming"
			:isEpisodeBuybox="isEpisodeBuybox"
			:seasonNumber="buyBoxSeasonNumber"
			:episodeNumber="episodeNumber"
			:hasVODOffers="hasVODOffers"
			@onNotifyMe="onNotifyMe"
		/>

		<p class="buybox-text">
			<span v-if="lastUpdated" class="buybox-container__timestamp">{{ timestampText }}</span>
			<OfferReporter
				:titleObjectId="buyBoxObjectId"
				:titleObjectType="buyBoxObjectType"
				:titleName="titleName"
				:titleId="buyBoxTitleId"
				:titleOffers="allRegularOffers"
				:additionalContexts="additionalContexts"
			/>
		</p>
	</div>
	<div class="buybox-loader" v-else>
		<div class="blocking-element"></div>
		<div class="blocking-element"></div>
	</div>
</template>

<script lang="ts" setup>
import { UpcomingRelease } from '@/@types/graphql-types';
import { getExcludedPackageShortNames } from '@/constants/package-exclusion';
import { BuyBoxOffers, useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import {
	GetBuyBoxOffersDocument,
	GetBuyBoxOffersQueryVariables,
	type GetBuyBoxOffersQuery,
} from '@/features/buybox/graphql/queries/GetBuyBoxOffers.query';
import { buyboxFilterByMonetizationType } from '@/features/buybox/helpers/list-buybox-config';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useInternationalOffersTracking } from '@/features/international-offers/tracking';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';

import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useTitleWatchlist } from '@/helpers/composables/useTitleWatchlist';
import { timestamp } from '@/helpers/date-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/useUserStoreTypes';
import type { EpisodeFragment } from '@/pages/graphql/fragments/Episode.fragment';
import { computed, provide, ref, watch } from 'vue';

// need to import this immediately because it's above the fold and could cause CLS
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';
import BuyBoxCountrySwitcher from '@/features/buybox/components/BuyBoxCountrySwitcher.vue';
import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';
import ListBuyBoxFilter from '@/features/buybox/components/list/ListBuyBoxFilter.vue';
import ListUpcomingNoOffers from '@/features/buybox/components/list/ListUpcomingNoOffers.vue';
import PromotedListBuyBox from '@/features/buybox/components/list/PromotedListBuyBox.vue';
import RegularListBuyBox from '@/features/buybox/components/list/RegularListBuyBox.vue';
import { useActivateExperiment, useExperiment } from '@/libraries/experiment';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';

const OfferReporter = defineAsyncComponent(() => import('@/features/buybox/components/OfferReporter.vue'));
const PlexPlayerModal = defineAsyncComponent(() => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'));
const BuyboxInternationalOffers = defineAsyncComponent(
	() => import('@/features/international-offers/components/BuyboxInternationalOffers.vue')
);

const emits = defineEmits({
	onWatchlistToggle: (createdAt: string | null) => ({ createdAt }),
});
const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		episode?: EpisodeFragment;
		buyboxOffers?: BuyBoxOffers | null;
		sponsoredAd?: SponsoredAdFragment | undefined;
		buyBoxSwitchType?: NewBuyBoxView;
		hideBuyBoxSwitcher?: boolean;
		additionalContexts: SnowplowContext[];
		episodeNumber?: number | null;
		placement?: ClickoutPlacement;
		upcomingEpisodeReleases?: UpcomingRelease[] | null;
		hidePromotedBuyBox?: boolean;
	}>(),
	{
		buyboxOffers: undefined,
		sponsoredAd: undefined,
		hideBuyBoxSwitcher: false,
		additionalContexts: () => [],
		placement: 'regular_buybox',
		upcomingEpisodeReleases: null,
		hidePromotedBuyBox: false,
	}
);

const { setInWatchlist, onIsLoggedInChange } = useTitleWatchlist(props.sponsoredAd);

const userStore = useUserStore();
const { setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic } = userStore;
const { newBuyboxFilterType, buyboxFilterTypeOptimistic, isLoggedIn, isProUser } = storeToRefs(userStore);

const { country, language } = storeToRefs(useLanguageStore());
const { titleProviderCount } = storeToRefs(useConstantStore());
const { inLists } = storeToRefs(useTitleDetailsStore());

const { t } = useI18n();
const { trackInternationalOffers } = useInternationalOffersTracking({ title: () => props.title });

useProvideClickoutPlacement(props.placement);

const { titleId, titleName, titleObjectId, titleObjectType, isTitleUpcoming, seasonNumber, isInWatchList } =
	useTitleDetailInfo(() => props.title);

const { activeVariantWithControlGroup: mergeBuyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

const buyBoxTitleId = computed(() => (props.episode ? props.episode.id : titleId.value));

const queryVariables = computed((): GetBuyBoxOffersQueryVariables => {
	return {
		nodeId: buyBoxTitleId.value ?? '',
		country: country.value,
		language: language.value,
		excludePackages: getExcludedPackageShortNames(
			isLoggedIn.value,
			buyBoxTitleId.value,
			country.value,
			isProUser.value
		),
		fallbackToForeignOffers: true,
	};
});

const { result, loading } = useQuery<GetBuyBoxOffersQuery, GetBuyBoxOffersQueryVariables>(
	GetBuyBoxOffersDocument,
	queryVariables,
	{
		enabled: !props.buyboxOffers,
		fetchPolicy: 'cache-first' as const,
		nextFetchPolicy: 'cache-first' as const,
	}
);

const buyboxData = computed(() => (!props.buyboxOffers ? (result.value?.node as BuyBoxOffers) : props.buyboxOffers));

const isEpisodeBuybox = computed(() => buyboxData.value?.__typename === 'Episode');

// episode buybox filter is not currently being stored. keep state for each Episode buybox at buybox level
const episodeBuyBoxType = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);
const episodeBuyBoxTypeOptimistic = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);

const buyboxFilterOptimistic = computed(() => {
	if (isEpisodeBuybox.value) {
		return episodeBuyBoxType.value;
	}
	return buyboxFilterTypeOptimistic.value ?? newBuyboxFilterType.value;
});
const buyboxFilter = computed(() => (isEpisodeBuybox.value ? episodeBuyBoxType.value : newBuyboxFilterType.value));

const buyboxUpcomingReleases = computed(() => {
	return props.upcomingEpisodeReleases ?? props.title.content.upcomingReleases;
});

const {
	filteredRegularOffers,
	allRegularOffers,
	upcomingReleases,
	upcomingReleasesFromGroups,
	promotedOffers,
	AVODOffer,
	dvdBluRayOffers,
	hasVODOffers,
} = useBuyBoxData({
	title: () => props.title,
	buyboxData,
	buyboxFilter,
	buyboxUpcomingReleases,
});

const buyboxContainer = ref<HTMLElement | null>(null);

// If the buybox is in the episodes list, use the episode id, objectId, objectType etc. instead of the title data
const buyBoxObjectId = computed(() => (props.episode ? props.episode.objectId : titleObjectId.value));
const buyBoxObjectType = computed(() => (props.episode ? props.episode.objectType : titleObjectType.value));
const buyBoxIsUpcoming = computed(() => (props.episode ? !props.episode.content.isReleased : isTitleUpcoming.value));
const buyBoxSeasonNumber = computed(() => (props.episode ? props.episode.content.seasonNumber : seasonNumber.value));

const { isActive: isInternationalOffersActive, isInternationalOffersTitle } = useInternationalOffersStatus({
	loading,
	title: () => props.title,
});

const buyBoxFilterOptions = computed(() => {
	const allOffers = allRegularOffers.value.concat(dvdBluRayOffers.value);
	return Object.keys(buyboxFilterByMonetizationType).filter(monetizationType => {
		// Only show Disc tab if there are physical offers present
		if (monetizationType === BuyBoxFilterOptions.PHYSICAL) {
			return dvdBluRayOffers.value.length > 0;
		}
		// Only show Buy/Rent tabs if there are non-physical Buy/Rent offers
		const offersToFilter =
			monetizationType === BuyBoxFilterOptions.BUY || monetizationType === BuyBoxFilterOptions.RENT
				? allRegularOffers.value
				: allOffers;

		return offersToFilter.some(offer =>
			buyboxFilterByMonetizationType[monetizationType as keyof typeof buyboxFilterByMonetizationType].includes(
				offer.monetizationType
			)
		);
	}) as BuyBoxFilterOptions[];
});

const titleContext = computed(() => {
	const title = props.title.__typename === 'Season' ? props.title.show : props.title;
	return new SnowplowTitleContextGraphql(
		title.objectId,
		title.objectType,
		buyBoxSeasonNumber.value ?? null,
		props.episodeNumber ?? null
	);
});

provide('titleContext', titleContext);
provide('titleSourceId', buyBoxTitleId.value);
provide('filterOption', buyboxFilter);

const { inPromotionCatalogue, promotionOffer } = usePromotion({ title: props.title, offers: allRegularOffers });

// EXP_SOFTER_FREE_TRIAL_V2
const { isArbitrating } = useArbitrage();
const activateSoftFreeTrialListV2 = computed(() => !isArbitrating.value && !isInternationalOffersTitle.value);
useActivateExperiment('EXP_SOFTER_FREE_TRIAL_V2', activateSoftFreeTrialListV2);
// EXP_SOFTER_FREE_TRIAL_V2

// DOUBLE_STACK_EXP
const { experimentInfo } = useExperiment('DOUBLE_STACK_EXP_3');
const isSessionVariant1 = computed(() => experimentInfo.value?.variant === 'variant_1');

const { isDoubleStackProvider } = useDoubleStackExp();
const activateDoubleStackExp = computed(() => {
	if (!promotionOffer.value || !isDoubleStackProvider(promotionOffer.value?.package.shortName)) return false;

	if (isSessionVariant1.value && !inLists.value.includes('EXP_PRIME_ORIGINALS')) return false;

	return inPromotionCatalogue.value;
});
useActivateExperiment('DOUBLE_STACK_EXP_3', activateDoubleStackExp);
// DOUBLE_STACK_EXP

// Underperforming Urls Exp

const activateUnderperformingUrlsExp = computed(
	() =>
		inLists.value.includes('EXP_LOWPERF_3') &&
		!props.title.offers.some(offer => ['amp', 'hlu'].includes(offer.package.shortName)) &&
		!inPromotionCatalogue.value
);

useActivateExperiment('EXP_UNDERPERFORMING_URLS', activateUnderperformingUrlsExp);

const lastUpdated = computed(() => {
	if (
		buyboxData.value?.__typename === 'Episode' ||
		buyboxData.value?.__typename === 'Movie' ||
		buyboxData.value?.__typename === 'Season' ||
		buyboxData.value?.__typename === 'Show'
	) {
		return buyboxData.value.maxOfferUpdatedAt;
	}
	return null;
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

async function onNotifyMe() {
	if (props.title && buyBoxTitleId.value) {
		const title = props.title.__typename === 'Season' ? props.title.show : props.title;

		const updatedTitle = await setInWatchlist(
			title.id,
			isInWatchList.value,
			{
				title: title.content.title,
				objectType: title.objectType,
				objectId: title.objectId,
			},
			{
				source: 'buybox',
			}
		);

		if (updatedTitle) {
			emits('onWatchlistToggle', updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt);
		}
	}
}

function trackInternationalOffersImpression(isVisible: boolean) {
	if (isVisible) {
		trackInternationalOffers('impression');
	}
}

watch(
	isLoggedIn,
	newValue => {
		if (!newValue) return;
		if (props.title && buyBoxTitleId.value) {
			onIsLoggedInChange(
				buyBoxTitleId.value || '',
				{
					title: props.title?.content.title,
					objectType: props.title?.objectType,
					objectId: props.title?.objectId,
				},
				isInWatchList.value,
				'buybox'
			);
		}
	},
	{ immediate: true }
);

async function handleFilterClick(payload: { value: BuyBoxFilterOptions; nonInteraction?: boolean }) {
	const optimisticUpdate = () => {
		if (isEpisodeBuybox.value) {
			episodeBuyBoxTypeOptimistic.value = payload.value;
		} else {
			setBuyboxFilterTypeOptimistic(payload.value);
		}
	};

	const commitUpdate = () => {
		if (isEpisodeBuybox.value) {
			episodeBuyBoxType.value = payload.value;
		} else {
			setNewBuyboxFilterType(payload.value);
		}
	};

	// updating the buybox is expensive.
	// that's why we split it up in 1) optimistic UI update
	optimisticUpdate();
	// then 2) give the JS thread time to render the optimistic UI update immediately
	await new Promise(resolve => setTimeout(resolve, 0));
	// and 3) defer the expensive store commit to the end
	commitUpdate();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'buybox',
			property: payload.value,
			nonInteraction: payload.nonInteraction,
		},
		props.additionalContexts
	);
}

function watchNowTitleClicked() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'click', label: 'watch_now_headline_clicked' },
		props.additionalContexts
	);

	if (!AVODOffer.value) {
		const element = buyboxContainer.value?.querySelector('#regular-buybox');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	} else {
		ModalHelper.openModal(
			PlexPlayerModal,
			{ offer: AVODOffer.value, title: props.title },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}
}
</script>

<style lang="scss" scoped>
.buybox-loader {
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: var(--spacing-08);

	:first-child {
		height: 200px;
	}

	:nth-child(2) {
		height: 400px;
	}
}
.buybox-container {
	display: flex;
	flex-direction: column;
	margin: 24px 0;

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;

		&__below-promoted {
			margin-bottom: 16px;
		}

		&__text {
			color: #fff;
			font-size: 16px;
			line-height: 24px;
			letter-spacing: 0.24px;
			margin: 0;
			text-transform: uppercase;
			cursor: pointer;

			&.primary-color {
				color: #fbc500;
			}

			&--upcoming {
				color: #ffffff;
			}
		}

		&__switchers {
			display: flex;
			align-items: center;
			gap: 8px;
		}
	}

	&__timestamp {
		font-size: 13.5px;
		margin: 0;
		margin-inline-end: 3px;
		border: none;
		color: #6f7d90;

		@media (max-width: 550px) {
			font-size: 11px;
		}
	}
}

.buybox-text {
	word-wrap: break-word;
	line-height: initial;
	margin-bottom: 0;
}

.upcoming-buybox {
	.offer-container:nth-child(2n) {
		background: #111924;
	}

	.offer-container:nth-child(2n-1) {
		background: #0c131e;
	}
}
</style>
